// Session Management Utility for Frontend
import { baseURL } from '../API/url';
import axios from 'axios';

if (process.env.NODE_ENV === 'production') {
  console.log = function () {};
  console.debug = function () {};
  console.info = function () {};
}
 
interface SessionData {
  sessionToken: string;
  expiresAt: string;
  user: any;
}

class SessionManager {
  private static instance: SessionManager;
  private sessionToken: string | null = null;
  private expiresAt: Date | null = null;
  private refreshTimer: NodeJS.Timeout | null = null;
  private warningTimer: NodeJS.Timeout | null = null;
  private isRefreshing: boolean = false; // Prevent multiple simultaneous refresh calls

  private constructor() {
    this.loadSessionFromStorage();
    this.setupActivityListeners();
  }

  public static getInstance(): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager();
    }
    return SessionManager.instance;
  }

  // Cached public IP of the user's browser (resolved once per session).
  private clientPublicIp: string | null = null;

  // Resolve the public IP address of the user's browser so the backend can use
  // the real client IP for the IP-change verification flow (rather than a proxy
  // or load-balancer address). Returns undefined if it cannot be determined.
  private async getClientPublicIp(): Promise<string | undefined> {
    if (this.clientPublicIp) {
      return this.clientPublicIp;
    }
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 3000);
      const response = await fetch('https://api.ipify.org?format=json', {
        signal: controller.signal,
      });
      clearTimeout(timeout);
      if (response.ok) {
        const data = await response.json();
        if (data && typeof data.ip === 'string' && data.ip.trim()) {
          const ip = data.ip.trim();
          this.clientPublicIp = ip;
          return ip;
        }
      }
    } catch (error) {
      console.warn('Unable to resolve client public IP:', error);
    }
    return undefined;
  }

  // Load session from localStorage
  private loadSessionFromStorage(): void {
    try {
      const sessionData = localStorage.getItem('ginkgo_session');
      console.log('Loading session from storage:', sessionData ? 'found' : 'not found');
      
      if (sessionData) {
        const parsed: SessionData = JSON.parse(sessionData);
        console.log('Parsed session data:', {
          hasToken: !!parsed.sessionToken,
          tokenLength: parsed.sessionToken?.length,
          expiresAt: parsed.expiresAt,
          hasUser: !!parsed.user
        });
        
        if (parsed.sessionToken && parsed.expiresAt) {
          this.sessionToken = parsed.sessionToken;
          this.expiresAt = new Date(parsed.expiresAt);
          
          // Validate the parsed date
          if (isNaN(this.expiresAt.getTime())) {
            console.error('Invalid expiresAt date in storage:', parsed.expiresAt);
            this.clearSession();
            return;
          }
          
          console.log('Session loaded - token length:', this.sessionToken.length, 'expiresAt:', this.expiresAt);
          
          // Check if session is expired
          if (this.expiresAt > new Date()) {
            console.log('Session is valid, starting refresh timer');
            this.startRefreshTimer();
          } else {
            console.log('Session is expired, clearing session');
            this.clearSession();
          }
        } else {
          console.log('Invalid session data format, clearing session');
          this.clearSession();
        }
      } else {
        console.log('No session data found in localStorage');
      }
    } catch (error) {
      console.error('Error loading session from storage:', error);
      this.clearSession();
    }
  }

  // Save session to localStorage
  private saveSessionToStorage(sessionData: SessionData): void {
    try {
      console.log('Saving session to localStorage:', sessionData);
      localStorage.setItem('ginkgo_session', JSON.stringify(sessionData));
      console.log('Session saved to localStorage successfully');
    } catch (error) {
      console.error('Error saving session to storage:', error);
    }
  }

  // Set up activity listeners for session refresh
  private setupActivityListeners(): void {
    // More specific events that indicate actual user interaction
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    let lastActivityTime = 0;
    const throttleDelay = 120000; // 2 minute throttle - only refresh once per 2 minutes max
    
    const refreshSession = (event: Event) => {
      const now = Date.now();
      if (now - lastActivityTime < throttleDelay) {
        return; // Throttle frequent calls
      }
      
      // Only refresh if session is valid and will expire in less than 1 minute
      if (this.sessionToken && this.isSessionValid() && this.expiresAt) {
        const timeUntilExpiry = this.expiresAt.getTime() - now;
        if (timeUntilExpiry < 60 * 1000) { // Less than 1 minute remaining
          lastActivityTime = now;
          console.log(`User activity detected (${event.type}), session expiring soon, refreshing...`);
          this.refreshSession();
        }
      }
    };

    events.forEach(event => {
      document.addEventListener(event, refreshSession, true);
    });
  }

  // Set session data after login
  public setSession(sessionData: SessionData): void {
    console.log('Setting session data:', {
      hasToken: !!sessionData.sessionToken,
      tokenLength: sessionData.sessionToken?.length,
      expiresAt: sessionData.expiresAt,
      user: sessionData.user
    });
    
    this.sessionToken = sessionData.sessionToken;
    this.expiresAt = new Date(sessionData.expiresAt);
    
    // Validate the dates
    if (isNaN(this.expiresAt.getTime())) {
      console.error('Invalid expiresAt date:', sessionData.expiresAt);
      this.expiresAt = new Date(Date.now() + 45 * 60 * 1000);
    }
    
    this.saveSessionToStorage(sessionData);
    this.startRefreshTimer();
    
    console.log('Session data stored successfully. Token length:', this.sessionToken?.length, 'Expires:', this.expiresAt);
  }

  // Get current session token
  public getSessionToken(): string | null {
/*     console.log('getSessionToken called - current state:', 
      {
      hasSessionToken: !!this.sessionToken,
      tokenLength: this.sessionToken?.length || 0,
      hasExpiresAt: !!this.expiresAt,
      expiresAt: this.expiresAt,
      currentTime: new Date(),
      isExpired: this.expiresAt ? this.expiresAt <= new Date() : true,
      localStorageExists: !!localStorage.getItem('ginkgo_session')
    }); */
    
    // Check localStorage if no in-memory session
    if (!this.sessionToken || !this.expiresAt) {
 //     console.log('No in-memory session, checking localStorage...');
      this.loadSessionFromStorage();
    }
    
    // First check if session is valid without logging too much
    if (!this.sessionToken || !this.expiresAt || this.expiresAt <= new Date()) {
      console.log('getSessionToken: Session invalid or expired', {
        hasToken: !!this.sessionToken,
        hasExpiresAt: !!this.expiresAt,
        isExpired: this.expiresAt ? this.expiresAt <= new Date() : 'no expiry date'
      });
      return null;
    }
    
//    console.log('getSessionToken: Returning valid token, length:', this.sessionToken.length);
    return this.sessionToken;
  }

  // Check if session is valid
  public isSessionValid(): boolean {
    const hasToken = this.sessionToken !== null && this.sessionToken.length > 0;
    const hasExpiresAt = this.expiresAt !== null;
    const notExpired = this.expiresAt !== null && this.expiresAt > new Date();
    const isValid = hasToken && hasExpiresAt && notExpired;
    
/*     console.log('isSessionValid check:', {
      hasToken,
      hasExpiresAt,
      notExpired,
      expiresAt: this.expiresAt,
      currentTime: new Date(),
      isValid,
      tokenLength: this.sessionToken?.length || 0
    });
 */    
    return isValid;
  }

  // Clear session
  public clearSession(): void {
    this.sessionToken = null;
    this.expiresAt = null;
    localStorage.removeItem('ginkgo_session');
    this.clearTimers();
  }

  // Start refresh timer
  private startRefreshTimer(): void {
    this.clearTimers();
    
    if (!this.expiresAt) return;

    // Only show warning 30 seconds before expiry, don't auto-refresh
    // Refresh should only happen on user activity
    const warningTime = this.expiresAt.getTime() - Date.now() - 30000;
    
    if (warningTime > 0) {
      this.warningTimer = setTimeout(() => {
        this.showExpiryWarning();
      }, warningTime);
    }

    // Set expiry timer to automatically logout when session expires
    const expiryTime = this.expiresAt.getTime() - Date.now();
    
    if (expiryTime > 0) {
      this.refreshTimer = setTimeout(() => {
        console.log('Session expired, logging out user');
        this.clearSession();
        window.location.href = '/login';
      }, expiryTime);
    }
  }

  // Clear all timers
  private clearTimers(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  // Refresh session
  private async refreshSession(): Promise<void> {
    if (!this.sessionToken || this.isRefreshing) return;

    this.isRefreshing = true;
    
    try {
      // console.log('Refreshing session...');
   //   console.log('Stack trace for refresh:', new Error().stack);
      const response = await fetch(`${baseURL}/auth/refresh-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.sessionToken}`,
          'Content-Type': 'application/json',
        },
      });      if (response.ok) {
        const data = await response.json();
        this.expiresAt = new Date(data.expiresAt);
        this.updateStorageExpiry();
        this.startRefreshTimer();
 //       console.log('Session refreshed successfully, new expiry:', this.expiresAt);
      } else if (response.status === 401) {
 //       console.log('Session refresh failed - token invalid, logging out');
        this.handleSessionExpiry();
      } else {
 //       console.log('Session refresh failed with status:', response.status);
        // Don't force logout on server errors, just log
      }
    } catch (error) {
      console.error('Error refreshing session:', error);
      // Don't force logout on network errors, just log
    } finally {
      this.isRefreshing = false;
    }
  }

  // Update expiry time in storage
  private updateStorageExpiry(): void {
    try {
      const sessionData = localStorage.getItem('ginkgo_session');
      if (sessionData && this.expiresAt) {
        const parsed = JSON.parse(sessionData);
        parsed.expiresAt = this.expiresAt.toISOString();
        localStorage.setItem('ginkgo_session', JSON.stringify(parsed));
      }
    } catch (error) {
      console.error('Error updating storage expiry:', error);
    }
  }

  // Show expiry warning
  private showExpiryWarning(): void {
    console.warn('Session will expire in 30 seconds - no user activity detected, will auto-logout');
    // No popup dialog - just let it expire automatically
  }

  // Handle session expiry
  private handleSessionExpiry(): void {
    console.log('Session expired, redirecting to login');
    this.clearSession();
    // Use replace to avoid adding to browser history and ensure we go to the correct port
    window.location.replace(window.location.origin + '/login');
  }

  // Login method - returns result object with status
  public async login(email: string, password: string): Promise<{ 
    success: boolean; 
    requiresIpVerification?: boolean; 
    userId?: string; 
    message?: string;
  }> {
    try {
      console.log('SessionManager login attempt:', { email, baseURL });
      const clientIp = await this.getClientPublicIp();
      const response = await fetch(`${baseURL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, clientIp }),
      });

      console.log('Login response status:', response.status);
      console.log('Login response headers:', response.headers);

      if (response.ok) {
        const data = await response.json();
        console.log('Login response data:', data);
        
        // Check if IP verification is required
        if (data.requiresIpVerification) {
          console.log('IP verification required');
          return {
            success: false,
            requiresIpVerification: true,
            userId: data.userId,
            message: data.message,
          };
        }
        
        console.log('Checking response format - sessionToken:', !!data.sessionToken, 'expiresAt:', !!data.expiresAt);
        
        // Handle session-based response
        if (data.sessionToken && data.expiresAt) {
          console.log('Using session-based response format');
          this.setSession({
            sessionToken: data.sessionToken,
            expiresAt: data.expiresAt,
            user: data.user,
          });
          console.log('Login successful - session data set');
          return { success: true };
        } 
        // Handle legacy JWT response (backward compatibility)
        else if (data.activationToken || data.id) {
          console.log('Received old JWT format, converting to session format');
          // Convert JWT response to session format with the inactivity fallback.
          const expiresAt = new Date(Date.now() + 45 * 60 * 1000).toISOString();
          this.setSession({
            sessionToken: data.activationToken || 'temp-session-token',
            expiresAt: expiresAt,
            user: data,
          });
          console.log('Login successful - converted JWT to session format');
          return { success: true };
        }
        
        console.error('Invalid response format - expected sessionToken/expiresAt or activationToken/id:', data);
        return { success: false, message: 'Invalid response format' };
      } else {
        const errorText = await response.text();
        console.error('Login failed with status:', response.status, 'Error:', errorText);
        return { success: false, message: 'Invalid credentials' };
      }
    } catch (error) {
      console.error('Error during login:', error);
      return { success: false, message: 'Login failed. Please try again.' };
    }
  }

  // Verify IP change and complete login
  public async verifyIpChange(userId: string, verificationCode: string): Promise<{
    success: boolean;
    requiresIpVerification?: boolean;
    message?: string;
  }> {
    try {
      console.log('SessionManager verifyIpChange attempt:', { userId });
      const clientIp = await this.getClientPublicIp();
      const response = await fetch(`${baseURL}/auth/verify-ip-change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId, verificationCode, clientIp }),
      });

      console.log('Verify IP response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Verify IP response data:', data);
        
        // Check if verification failed (wrong code, expired, etc.)
        if (data.requiresIpVerification) {
          console.log('IP verification still required:', data.message);
          return {
            success: false,
            requiresIpVerification: true,
            message: data.message,
          };
        }
        
        // Verification successful, set session
        if (data.sessionToken && data.expiresAt) {
          console.log('IP verification successful, setting session');
          this.setSession({
            sessionToken: data.sessionToken,
            expiresAt: data.expiresAt,
            user: data.user,
          });
          return { success: true };
        }
        
        console.error('Invalid verification response format:', data);
        return { success: false, message: 'Invalid response format' };
      } else {
        const errorText = await response.text();
        console.error('Verification failed with status:', response.status, 'Error:', errorText);
        return { success: false, message: 'Verification failed. Please try again.' };
      }
    } catch (error) {
      console.error('Error during IP verification:', error);
      return { success: false, message: 'Verification failed. Please try again.' };
    }
  }

  // Logout method
  public async logout(isSessionExpired: boolean = false): Promise<void> {
    if (this.sessionToken) {
      try {
        await fetch(`${baseURL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.sessionToken}`,
            'Content-Type': 'application/json',
          },
        });
      } catch (error) {
        console.error('Error during logout:', error);
      }
    }
    
    this.clearSession();
    
    // Store session expiration flag if logout is due to session expiry
    if (isSessionExpired) {
      localStorage.setItem('session_expired', 'true');
    }
    
    window.location.replace(window.location.origin + '/login');
  }
}

// Export singleton instance
export const sessionManager = SessionManager.getInstance();

// Flag to prevent duplicate interceptor setup
let interceptorsSetup = false;

// Axios interceptor for automatic session token injection
export const setupAxiosInterceptors = (axios: any) => {
  // Prevent duplicate setup
  if (interceptorsSetup) {
    console.log('Axios interceptors already set up, skipping...');
    return;
  }
  
  console.log('Setting up axios interceptors...');
  interceptorsSetup = true;
  
  // Request interceptor to add session token
  axios.interceptors.request.use(
    (config: any) => {
      const token = sessionManager.getSessionToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
//        console.log('Axios interceptor - added auth header for:', config.url);
      } else {
 //       console.log('Axios interceptor - no valid token available for:', config.url);
      }
      return config;
    },
    (error: any) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle session expiry
  axios.interceptors.response.use(
    (response: any) => response,
    async (error: any) => {
      if (error.response?.status === 401) {
        console.log('Unauthorized request detected (401):', error.config?.url);
        console.log('Session token expired, logging out...');
        
        // Call logout with session expired flag
        await sessionManager.logout(true);
      }
      return Promise.reject(error);
    }
  );
};

// Automatically set up interceptors on the default axios instance
setupAxiosInterceptors(axios);

export default sessionManager;
